import { useState, useEffect, useRef } from "react";
import { safeArray, formatDescription } from "../utils/text";
import { PlaceholderImage } from "../utils/imageHolder";
import { Badge } from "../utils/imageHolder";
import "./../ImageAnalysisModalNew.css";
import { modelsApi } from "../../../../API/axiosInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft, faChevronRight, faXmark, faSpinner,
} from "@fortawesome/free-solid-svg-icons";


// Modal Component
export function Modal({ open, onClose, itemId, onPrev, onNext }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setError(null);
    setItem(null);

    const controller = new AbortController();

    modelsApi
      .get(`/result/${itemId}`, {
        signal: controller.signal, // ✅ keep cancel support
      })
      .then((response) => {
        setItem(response.data);
      })
      .catch((err) => {
        // axios cancel error handling
        if (err.name === "CanceledError" || err.name === "AbortError") return;

        console.error(err);
        setError(err.message);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [open, itemId]);
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev && onPrev();
      if (e.key === "ArrowRight") onNext && onNext();
    }
    if (!open) return;
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, onPrev, onNext]);

  if (!open) return null;

  return (
    <div className="image-modal-overlay">
      <div className="image-modal-backdrop" onClick={onClose} />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        className="image-modal-dialog"
      >
        {/* Header */}
        <div className="image-modal-header">
          <div className="image-modal-header-content">
            <div className="image-modal-nav-buttons">
              <button onClick={onPrev} className="image-modal-glass-button">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button onClick={onNext} className="image-modal-glass-button">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <h2 className="image-modal-title">Content Details</h2>
            </div>
            <button onClick={onClose} className="image-modal-glass-button">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="image-modal-content">
          <div className="image-modal-grid">
            {/* Left Column */}
            <div className="image-modal-left-column">
              {/* Image */}
              <div className="image-modal-image-wrapper">
                {loading ? (
                  <div className="image-modal-loading">
                    <FontAwesomeIcon icon={faSpinner} spin className="image-modal-spinner" />
                  </div>
                ) : item && item.image_url ? (
                  <a href={item.image_url} target="_blank" rel="noreferrer">
                    <img
                      src={item.image_url}
                      alt={item._id}
                      className="image-modal-image"
                    />
                  </a>
                ) : (
                  <PlaceholderImage style={{ width: "100%", height: "100%" }} />
                )}
              </div>

              {/* Info Card */}
              <div className="image-modal-info-card">
                <div className="image-modal-info-item">
                  <span className="image-modal-info-label">ID</span>
                  <div className="image-modal-info-value">
                    {item?._id || itemId}
                  </div>
                </div>
                <div className="image-modal-info-item">
                  <span className="image-modal-info-label">Status</span>
                  <div style={{ marginTop: "0.5rem" }}>
                    <span className="image-modal-status-badge">
                      <span className="image-modal-status-dot"></span>
                      {item?.status || "—"}
                    </span>
                  </div>
                </div>
                {/* <div className="image-modal-info-item">
                  <span className="image-modal-info-label">Languages</span>
                  <div className="image-modal-language-badges">
                    {safeArray(item?.lang?.detected_languages).length > 0 ? (
                      safeArray(item.lang.detected_languages).map((l) => (
                        <span key={l} className="image-modal-language-badge">
                          {l}
                        </span>
                      ))
                    ) : (
                      <span className="image-modal-language-badge">
                        Unknown
                      </span>
                    )}
                  </div>
                </div> */}
              </div>
            </div>

            {/* Right Column */}
            <div>
              {/* Tabs */}
              <div className="image-modal-tabs">
                {["overview", "categories"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`image-modal-tab-button ${
                      activeTab === tab ? "active" : ""
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Loading State */}
              {loading && (
                <div
                  className="image-modal-loading"
                  style={{ height: "16rem" }}
                >
                  <i
                    className="fas fa-spinner fa-spin image-modal-spinner"
                    style={{ fontSize: "4rem" }}
                  ></i>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="image-modal-error-card">
                  <p className="image-modal-error-text">Error: {error}</p>
                </div>
              )}

              {/* Content */}
              {item && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                  }}
                >
                  {/* Overview Tab */}
                  {activeTab === "overview" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.5rem",
                      }}
                    >
                      <div className="image-modal-content-card">
                        <h3>Caption / Description</h3>
                        <div className="image-modal-content-text">
                          {formatDescription(item.caption?.description)}
                        </div>
                      </div>
                      <div className="image-modal-stats-grid">
                        <div className="image-modal-stat-card">
                          <div className="image-modal-stat-value primary">
                            {safeArray(item.category?.categories).length}
                          </div>
                          <div className="image-modal-stat-label">
                            Categories
                          </div>
                        </div>
                        {/* <div className="image-modal-stat-card">
                          <div className="image-modal-stat-value secondary">
                            {safeArray(item.lang?.detected_languages).length}
                          </div>
                          <div className="image-modal-stat-label">
                            Languages
                          </div>
                        </div> */}
                      </div>
                    </div>
                  )}

                  {/* Text Tab */}
                  {/* {activeTab === "text" && (
                    <div className="image-modal-text-content-card">
                      <h3>Extracted Text</h3>
                      <pre className="image-modal-text-content-pre">
                        {item.lang?.extracted_text || "—"}
                      </pre>
                    </div>
                  )} */}

                  {/* Categories Tab */}
                  {activeTab === "categories" && (
                    <div className="image-modal-category-list">
                      {safeArray(item.category?.categories).length === 0 && (
                        <div className="image-modal-empty-state">
                          No categories available
                        </div>
                      )}
                      {safeArray(item.category?.categories).map((c, idx) => (
                        <div key={c} className="image-modal-category-item">
                          <div className="image-modal-category-header">
                            <div className="image-modal-category-name">{c}</div>
                            <div className="image-modal-category-number">
                              #{idx + 1}
                            </div>
                          </div>
                          <div className="image-modal-category-explanation">
                            {safeArray(item.category?.explanations)[idx] ||
                              "No explanation provided."}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
